<script setup>
import Swiper from 'swiper';
import { defineEmits, defineProps, nextTick, onMounted, ref } from 'vue';
import Icon from '../../miscellaneous/Icon.vue';

const props = defineProps({
    fullScreenScope: {
        type: String,
        default: 'slider'
    },
    images: {
        type: Array,
        required: true
    },
    activeModalTab: {
        type: Number,
        default: 0
    },

    tabContent: {
        type: Array,
        default: () => []
    },
    imageSliderInitValue: {
        type: Number,
        default: 0
    }
});

const emit = defineEmits(['toggleFullscreen', 'changeActiveTabState', 'slideToImage']);
const closeFullScreen = () => {
    emit('toggleFullscreen', props.fullScreenScope);
};
const showFullScreenText = ref(true);
const isDragging = ref(false);
const slider = ref(null);
const xRef = ref(10);
const yRef = ref(10);
const mouseXPos = ref(0);
const mouseYPos = ref(0);
const activeTab = ref(props.activeModalTab || 0);

window.addEventListener('mousemove', (e) => {
    if (isDragging.value) {
        xRef.value += e.clientX - mouseXPos.value;
        yRef.value += e.clientY - mouseYPos.value;
    }

    mouseXPos.value = e.clientX;
    mouseYPos.value = e.clientY;
});

const slideToImage = (index) => {
    if (slider.value?.slideTo) {
        slider.value.slideTo(index);
        emit('slideToImage', index);
    }
};

const changeActiveTab = (item, index) => {
    if (props.fullScreenScope === 'slider') {
        const hasImageValue = props.images.findIndex((image) => image.key === item?.attributes?.images[0]?.key);

        slideToImage(hasImageValue !== -1 ? hasImageValue : 0);
    } else {
        slideToImage(index);
    }
};
const changeActiveTabFromButton = (item, index) => {
    if (props.fullScreenScope === 'slider') {
        const hasImageValue = props.images.findIndex((image) => image.key === item?.key);
        slideToImage(hasImageValue !== -1 ? hasImageValue : 0);

        const getFirstName = props.tabContent.findIndex((pos) => {
            const tabTitle = pos.attributes.title.de;
            const itemTitle = item?.attributes.title.de;

            return itemTitle?.includes(tabTitle);
        });

        activeTab.value = getFirstName !== -1 ? getFirstName : 0;
    } else {
        activeTab.value = Number(index);
        slideToImage(index);
    }
};

const toggleShowFullScreenText = () => (showFullScreenText.value = !showFullScreenText.value);

const isDraggingActive = (value) => (isDragging.value = value || false);

const initSlider = async () => {
    await nextTick();
    slider.value = new Swiper('.image-slider-fullscreen', {
        slidesPerView: 1,
        spaceBetween: 10,
        navigation: true,
        pagination: {
            el: '.swiper-pagination',
            clickable: true
        }
    });

    slider.value.slideTo(props.imageSliderInitValue || 0);
};
onMounted(() => {
    initSlider();
});
</script>

<template>
    <div ref="image_preview" class="fixed inset-0 z-50 overflow-hidden bg-transparent backdrop-blur-md">
        <div class="z-50 flex items-center fixed top-2 right-2 justify-between p-2 shadow-md bg-white opacity-30 hover:opacity-100 rounded-lg">
            <div class="flex items-center gap-4">
                <button
                    class="w-8 h-8 flex items-center justify-center rounded-xl border border-blue text-blue hover:bg-blue/70 hover:text-white duration-300 cursor-pointer"
                    @click="$refs.zoomer.zoomIn(1.5)"
                >
                    <Icon type="Search" viewBox="0 0 19 20" width="12" height="12" class="fill-current" />
                    <span class="font-light">+</span>
                </button>

                <button
                    class="w-8 h-8 flex items-center justify-center rounded-xl border border-blue text-blue hover:bg-blue/70 hover:text-white duration-300 cursor-pointer"
                    @click="$refs.zoomer.zoomOut(0.5)"
                >
                    <Icon type="Search" viewBox="0 0 19 20" width="12" height="12" class="fill-current" />
                    <span class="font-light">-</span>
                </button>

                <button
                    class="w-8 h-8 flex items-center justify-center rounded-xl border border-blue text-blue hover:bg-blue/70 hover:text-white duration-300 cursor-pointer"
                    @click="closeFullScreen"
                >
                    <i class="fi fi-ss-cross-small flex items-center justify-center w-full h-full"></i>
                </button>
            </div>
        </div>

        <div class="dragzone relative">
            <div
                :style="{
                    position: 'absolute',
                    left: `${xRef}px`,
                    top: `${yRef}px`
                }"
                draggable="false"
            >
                <div
                    class="border bg-white/95 min-w-[60px] min-h-[60px] p-5 rounded-2xl border-gray-300 block transition-opacity duration-300"
                    :class="{
                        'opacity-30 hover:opacity-100': !showFullScreenText,
                        'opacity-100': showFullScreenText
                    }"
                >
                    <div class="flex justify-between">
                        <button
                            class="w-8 h-8 flex items-center justify-center rounded-xl border border-blue text-blue hover:bg-blue/70 hover:text-white duration-300 cursor-pointer"
                            @click="toggleShowFullScreenText"
                        >
                            <i :class="showFullScreenText ? 'fi fi-ss-minus' : 'fi fi-ss-plus'" class="text-sm leading-none flex items-center justify-center w-5 h-5"></i>
                        </button>

                        <button
                            v-show="showFullScreenText"
                            :class="{
                                'cursor-grabbing': isDragging,
                                'cursor-grab': !isDragging
                            }"
                            class="drag-handlew-8 h-8 flex items-center justify-center rounded-xl bg-blue-900 text-blue"
                            @mousedown="isDraggingActive(true)"
                            @mouseup="isDraggingActive(false)"
                        >
                            <i class="fi fi-ss-arrows"></i>
                        </button>
                    </div>

                    <div v-show="showFullScreenText" class="w-[30rem]">
                        <Tabs
                            v-if="tabContent.length > 1"
                            :data="tabContent"
                            :active-tab="activeTab || 0"
                            active-color="#353CEE"
                            :first-on-load="true"
                            @update:active-tab="
                                (item, key) => {
                                    changeActiveTab(item, key);
                                }
                            "
                        >
                            <template #header="{ data }">
                                <strong class="whitespace-nowrap">{{ $translate(data?.attributes.title) ? $translate(data?.attributes?.title) : $t('plain_analysis') }}</strong>
                            </template>
                            <template #item="{ _, data }">
                                <div v-if="data.attributes?.wysiwyg" class="content fr-view overflow-y-scroll max-h-[60vh] p-2" v-html="$translate(data.attributes.wysiwyg)"></div>
                            </template>
                        </Tabs>
                        <div v-else>
                            <div v-if="tabContent[0]?.attributes?.wysiwyg">
                                <div class="content fr-view overflow-y-scroll max-h-[60vh] p-2" v-html="$translate(tabContent[0].attributes.wysiwyg)"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="images.length">
            <div class="relative">
                <!-- Image Slider -->
                <v-zoomer ref="zoomer" :max-scale="10" :zooming-elastic="false" class="md:h-screen pb-24">
                    <div class="swiper-container image-slider-fullscreen">
                        <div class="swiper-wrapper">
                            <div v-for="item in images" :key="item.id" class="swiper-slide">
                                <img class="rounded prevent-click h-[100vh] w-full" :src="item.attributes.image" alt="slider-image" />
                            </div>
                        </div>
                    </div>
                </v-zoomer>

                <!-- Image Title Click -->
            </div>
        </div>

        <div class="fixed left-0 right-0 z-20 bottom-0 bg-transparent shadow-md">
            <div class="flex max-w-full gap-2 p-2 mx-auto overflow-x-auto w-fit bg-white rounded-md">
                <button
                    v-for="(item, index) in images"
                    :key="item.id"
                    :class="index === slider?.activeIndex || 0 ? 'bg-blue text-white' : 'text-blue hover:scale-105 hover:shadow-md duration-300'"
                    class="p-2 border rounded-md shadow-md border-blue whitespace-nowrap"
                    @click="changeActiveTabFromButton(item, index)"
                >
                    {{ $translate(item.attributes.title) }}
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.dragzone {
    width: 100vw;
    z-index: 200;
    position: relative;
}

v-zoomer {
    padding-bottom: 6rem;
}
</style>
